import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { Route, Switch, Redirect } from 'react-router-dom';
import { loadReCaptcha } from 'react-recaptcha-google';
import { deepmerge } from '@mui/utils';

import MainApp from 'routes/app/';
import Page404 from 'routes/404/';
import Page500 from 'routes/500/';
import PageConfirmEmail from 'routes/confirm-email/';
import PageResetPassword from 'routes/reset-password';
import PageForgotPassword from 'routes/forgot-password/';
import PageFullscreen from 'routes/fullscreen/';
import PrivacyPolice from 'routes/privacy-policy';
import PageLockScreen from 'routes/lock-screen/';
import PageLogin from 'routes/login/';
import PageSignUp from 'routes/sign-up/';
import TrackingOrder from 'routes/tracking-order';
import CashboxApproval from 'routes/cashbox-approval';
import LoginLink, { LoginLinkCodePage } from 'routes/login-link';
import LoginLinkWatcher from 'routes/login-link/LoginLinkWatcher';
import PageSignUpIn from 'routes/sign-up-in/';
import { isCustomer, isSuperAdmin } from 'utils/helpers';
import MaintenancePage from '../routes/maintenance/MaintenancePage';
import PageShopifyLogin from '../routes/shopify-login';

// = styles =
// 3rd
import 'styles/bootstrap.scss';
// custom
import 'styles/layout.scss';
import 'styles/theme.scss';
import 'styles/ui.scss';
import 'styles/app.scss';

import lightTheme from './themes/lightTheme';
import darkTheme from './themes/darkTheme';
import grayTheme from './themes/grayTheme';
import muiComponentsTheme from './themes/muiComponentsTheme';
import {
  EMAIL_SUBSCRIPTION_PAGE_PATH,
  CASHBOX_APPROVAL_PAGE_PATH,
  CASHBOX_APPROVAL_TOKEN_KEY,
  LOGIN_LINK_PAGE_PATH,
  LOGIN_LINK_CUSTOMER_APP_PAGE_PATH,
  LOGIN_LINK_CODE_PAGE_PATH,
} from 'constants/Paths';
import RedirectToApp from '../routes/RedirectToApp/RedirectToApp';
import RedirectToMarketplace from '../routes/RedirectToMarketplace/RedirectToMarketplace';
import * as moduleService from 'services/moduleService';

class App extends Component {

  handleUriParameter = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const uri = urlParams.get('request_uri');

    if (uri) {
      const decodedUri = decodeURIComponent(uri);
      this.props
        .dispatch(moduleService.getAllowedModules())
        .then(modules => {
          const matchingModule = modules.find(module => module.link === decodedUri);
          if (matchingModule) {
            this.props.dispatch(moduleService.updateSelectedModule(matchingModule.id));
          }
        });
    }
  }
  componentDidMount() {
    loadReCaptcha();
  }

  render() {
    const {
      match,
      location,
      layoutBoxed,
      navCollapsed,
      navBehind,
      fixedHeader,
      sidebarWidth,
      theme,
      user,
    } = this.props;
    let materialUITheme;
    switch (theme) {
      case 'gray':
        materialUITheme = grayTheme;
        break;
      case 'dark':
        materialUITheme = darkTheme;
        break;
      default:
        materialUITheme = lightTheme;
    }
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE;
    if (isMaintenanceMode == 'true') {
      return <MaintenancePage />;
    }
    const isRoot = location.pathname === '/' ? true : false;
    if (location.pathname == EMAIL_SUBSCRIPTION_PAGE_PATH) {
      localStorage.setItem('lastPageVisited', EMAIL_SUBSCRIPTION_PAGE_PATH);
    }

    if (isRoot) {
      const shopifyId = user?.customer?.third_party?.shopify?.[0]?.id;

      if (process.env.REACT_APP_IS_SHOPIFY === 'true') {
        let is_authenticated = localStorage.getItem('jwt_token');
        if (!is_authenticated) {
          return <Redirect to={'/login'} />;
        }
        if (shopifyId) {
          return <Redirect to={`/app/third_party/shopify/${shopifyId}`} />;
        }
      } else {
        let path = '/login';
        this.handleUriParameter();
        if (!_.isEmpty(user)) {
          path = isSuperAdmin(user) ? '/app/organizations/management/list' : isCustomer(user)? '/app/orders' : '/app/orders/pending';
        }
        return <Redirect to={path} />;
      }
    }

    return (
      <ThemeProvider theme={createTheme(deepmerge(materialUITheme, muiComponentsTheme))}>
        <div id="app-inner">
          {process.env.REACT_APP_IS_SHOPIFY !== 'true' && <LoginLinkWatcher />}
          <div className="preloaderbar hide">
            <span className="bar" />
          </div>
          <div
            className={classnames('app-main full-height', {
              'fixed-header': fixedHeader,
              'nav-collapsed': navCollapsed,
              'nav-behind': navBehind,
              'layout-boxed': layoutBoxed,
              'theme-gray': theme === 'gray',
              'theme-dark': theme === 'dark',
              'sidebar-sm': sidebarWidth === 'small',
              'sidebar-lg': sidebarWidth === 'large',
            })}
          >
            <Switch>
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/confirm-email"
                  component={PageConfirmEmail}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/privacy-policy"
                  component={PrivacyPolice}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path={["/reset-password", "/staff-reset-password"]}
                  component={PageResetPassword}
                />
              )}
              <Route
                exact
                strict
                path="/forgot-password"
                component={PageForgotPassword}
              />
              <Route
                exact
                strict
                path={`/marketplace/item/:id(\\d+)`}
                component={RedirectToApp}
              />
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path={`${LOGIN_LINK_CUSTOMER_APP_PAGE_PATH}/:token([a-f0-9]{64})`}
                  render={(props) => <LoginLink {...props} isCustomerApp />}
                />
              )}
              <Route
                exact
                strict
                path={`/customer-app`}
                component={RedirectToMarketplace}
              />
              {process.env.REACT_APP_CONTEXT && process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/sign-up-in"
                  component={PageSignUpIn}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/orders/:number([a-zA-Z0-9]+)"
                  component={TrackingOrder}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/orders/:number([a-zA-Z0-9]+)/:code([a-zA-Z0-9.$]+)"
                  component={TrackingOrder}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path={`${CASHBOX_APPROVAL_PAGE_PATH}/:token([a-zA-Z0-9]+)`}
                  component={CashboxApproval}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path={`${LOGIN_LINK_PAGE_PATH}/:token([a-f0-9]{64})`}
                  component={LoginLink}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path={LOGIN_LINK_CODE_PAGE_PATH}
                  component={LoginLinkCodePage}
                />
              )}
              {process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route
                  exact
                  strict
                  path="/lock-screen"
                  component={PageLockScreen}
                />
              )}
              {process.env.REACT_APP_CONTEXT && process.env.REACT_APP_IS_SHOPIFY !== 'true' && (
                <Route exact strict path="/sign-up" component={PageSignUp} />
              )}

              <Route path={`${match.url}app`} component={MainApp} />
              <Route exact strict path="/404" component={Page404} />
              <Route exact strict path="/500" component={Page500} />
              <Route exact strict path="/shopify" component={PageFullscreen} />
              {process.env.REACT_APP_IS_SHOPIFY === 'true' && (
                <Route
                  exact
                  strict
                  path="/shopify/login"
                  component={PageShopifyLogin}
                />
              )}
             
              <Route 
                exact 
                strict 
                path="/login" 
                render={(props) => {
                  if (!_.isEmpty(user)) {
                    this.handleUriParameter();

                    const cashbox_approval_token = sessionStorage.getItem(
                      CASHBOX_APPROVAL_TOKEN_KEY,
                    );
                    if (cashbox_approval_token) {
                      return (
                        <Redirect
                          to={`${CASHBOX_APPROVAL_PAGE_PATH}/${cashbox_approval_token}`}
                        />
                      );
                    }

                    let redirectPath = '/';
                    if (isSuperAdmin(user)) {
                      redirectPath = '/app/organizations/management/list';
                    } else if (isCustomer(user)) {
                      if (process.env.REACT_APP_IS_SHOPIFY === 'true') {
                        const shopifyId = user?.customer?.third_party?.shopify?.[0]?.id;
                        redirectPath = shopifyId 
                          ? `/app/third_party/shopify/${shopifyId}` 
                          : '/';
                      } else {
                        redirectPath = '/app/orders';
                      }
                    }
                    return <Redirect to={redirectPath} />;
                  }
                  return <PageLogin {...props} />;
                }} 
              />
              <Redirect
                to={_.isEmpty(user) || user == {} ? '/login' : '/404'}
              />            
            </Switch>
          </div>
        </div>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  layoutBoxed: state.settings.layoutBoxed,
  navCollapsed: state.settings.navCollapsed,
  navBehind: state.settings.navBehind,
  fixedHeader: state.settings.fixedHeader,
  sidebarWidth: state.settings.sidebarWidth,
  theme: state.settings.theme,
  user: state.auth.user,
});

export default connect(mapStateToProps)(App);
